'use client';

import { useState, useEffect, useRef, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from './Dialog';
import { cn } from '@/shared/lib/utils';

interface InputDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (value: string) => void | Promise<void>;
	title: string;
	description: ReactNode;
	inputLabel?: string;
	inputPlaceholder: string;
	initialValue?: string;
	cancelButtonText: string;
	submitButtonText: string;
	isLoading?: boolean;
	loadingText?: string;
	validateValue?: (value: string) => boolean;
}

export function InputDialog({
	isOpen,
	onClose,
	onSubmit,
	title,
	description,
	inputPlaceholder,
	initialValue = '',
	cancelButtonText,
	submitButtonText,
	isLoading = false,
	loadingText,
	validateValue,
}: InputDialogProps) {
	const [value, setValue] = useState(initialValue);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isOpen) {
			setValue(initialValue);
			setTimeout(() => {
				if (inputRef.current) {
					inputRef.current.focus();
					inputRef.current.select();
				}
			}, 100);
		}
	}, [isOpen, initialValue]);

	const isValid = validateValue ? validateValue(value) : value.trim() !== '';

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (isValid && !isLoading) {
			await onSubmit(value.trim());
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
				</DialogHeader>

				<DialogDescription>{description}</DialogDescription>

				<form onSubmit={handleSubmit}>
					<div className="py-4">
						<Input
							ref={inputRef}
							type="text"
							value={value}
							onChange={(e) => setValue(e.target.value)}
							className="w-full bg-white/5 border-white/10 text-white placeholder:text-white/40"
							placeholder={inputPlaceholder}
							autoComplete="off"
							disabled={isLoading}
						/>
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="ghost"
							onClick={onClose}
							disabled={isLoading}
							className="text-white/80 hover:bg-white/10"
						>
							{cancelButtonText}
						</Button>
						<Button
							type="submit"
							disabled={!isValid || isLoading}
							className={cn(
								'bg-red-500/20 text-red-400 border border-red-400/30 hover:bg-red-500/30',
								(!isValid || isLoading) &&
									'opacity-50 cursor-not-allowed'
							)}
						>
							{isLoading && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{isLoading && loadingText
								? loadingText
								: submitButtonText}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
